// ─────────────────────────────────────────────────────────────────────────────
// LabThree — Guida "Registrare i monitoraggi"
// Centro assistenza → guida per operatori (HACCP, tutti i piani).
//
// E' la schermata che un operatore apre ogni mattina, quindi e' quella dove una
// guida sbagliata fa piu' danni: la legge chi non ha voglia di leggere.
//
// ATTENZIONE, ERRORE GIA' COMMESSO UNA VOLTA (17/08/2026): la sessione di
// registrazione NON e' di gruppo. E' uno stato locale del componente: personale,
// per scheda del browser, e si perde ricaricando la pagina. Non influisce sugli
// altri operatori. Se qualcuno riscrive quel capitolo come "il responsabile
// avvia la sessione per tutti", e' di nuovo sbagliato.
//
// Schermate RICOSTRUITE (vedi la nota in atomi.js). Etichette dal codice.
//
// Riferimenti nel codice, per chi dovra' aggiornarla:
//   Schermata ...... App.js, Attivita — titolo letterale "Attività da eseguire"
//   Viste .......... segmenti "Classica" / "Settimanale"; la scelta e' ricordata
//                    in localStorage (labthree_vista_attivita_modo)
//   Pulsanti ....... "Conforme" (verde) · "Non conforme" (rosso) ·
//                    "N.A. — non applicabile oggi" (testo piccolo a destra;
//                    il suggerimento del mouse aggiunge "(es. macchina non in
//                    uso)"). Chiama /attivita/salta -> saltata=true.
//   Card ........... il titolo grande e' `oggetto`, la riga sotto e'
//                    `collocazione · tipo_attivita · sub_attivita`. Non
//                    invertirli: e' un errore gia' fatto in bozza.
//                    Sopra: chip "Ogni Ngg" (o "Controllo continuo") e, se in
//                    ritardo, "In ritardo +Ngg".
//   Allegato ....... se l'attivita' ha registro_speciale, DOPO la
//                    registrazione si apre ModalProponiAllegato: "Carica
//                    rapporto di taratura / di manutenzione / referto di
//                    analisi / rapporto di verifica".
//   Valori ......... FormValore. Se l'attivita' ha valore_min/valore_max chiede
//                    "Valore misurato" e RIFIUTA l'incoerenza:
//                    "Valore fuori dai limiti (min: X, max: Y) — non puoi
//                     registrare come CONFORME" e viceversa per NON CONFORME.
//   Sessione ....... pulsante "Avvia sessione" (in alto a destra) -> modale
//                    intitolata "Avvia sessione di registrazione", campi Data
//                    e Ora -> stato `sessione` { data, ora }, banner "Sessione
//                    attiva" con pulsante "Termina". LOCALE, vedi avvertenza.
//   Ritardo ........ chip "In ritardo +Ngg" e bordo rosso sulla card
//   Mostra tutto ... casella che ricarica con ?tutto=1
//   Correzione ..... NON si fa qui: si fa nello Storico, entro 7 giorni
//                    (GIORNI_FINESTRA_CORREZIONE), solo sulle proprie e solo se
//                    non ancora verificate. Guida #8.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import LT from '../../ui/tokens';
import I, { Ico } from '../../ui/icons';
import {
  GuidaLayout, H, P, Strong, Code, Tasto, Callout, Passo,
  MockFrame, Schermo, Segmenti, FAQ,
} from './atomi';

const SEZIONI = [
  { id: 'm0', label: 'La schermata di ogni giorno' },
  { id: 'm1', label: '1. Leggere la lista' },
  { id: 'm2', label: '2. I tre pulsanti' },
  { id: 'm3', label: '3. Quando chiede un valore' },
  { id: 'm4', label: '4. La sessione di registrazione' },
  { id: 'm5', label: '5. La vista settimanale' },
  { id: 'm6', label: '6. Correggere un errore' },
  { id: 'm7', label: 'Le trappole' },
  { id: 'm8', label: 'Domande frequenti' },
];

// ─── Pezzi di schermata ──────────────────────────────────────────────────────

const ChipFinto = ({ children, tono = 'neutro' }) => {
  const c = {
    verde:   { fg: LT.verde,   bg: LT.verdeSoft },
    rosso:   { fg: LT.rosso,   bg: LT.rossoSoft },
    arancio: { fg: LT.arancio, bg: LT.arancioSoft },
    blu:     { fg: LT.blu,     bg: LT.bluSoft },
    viola:   { fg: LT.viola,   bg: LT.violaSoft },
    neutro:  { fg: LT.n700,    bg: LT.n100 },
  }[tono];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 9px', borderRadius: 999, background: c.bg, color: c.fg,
      fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap',
    }}>{children}</span>
  );
};

const BtnFinto = ({ children, tono = 'neutro', pieno }) => {
  const c = {
    verde:  LT.verde, rosso: LT.rosso, blu: LT.blu, neutro: LT.n400,
  }[tono];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
      padding: '9px 14px', borderRadius: LT.r.md, fontSize: 13, fontWeight: 700,
      background: pieno ? c : LT.n0,
      color: pieno ? '#fff' : (tono === 'neutro' ? LT.n700 : c),
      border: `1px solid ${pieno || tono !== 'neutro' ? c : LT.n300}`,
      whiteSpace: 'nowrap',
    }}>{children}</span>
  );
};

// Tessera colorata della categoria, come CatTile in ui/components.
const Tessera = ({ cat, size = 40 }) => {
  const s = LT.cat[cat] || LT.cat.temperature;
  return (
    <div style={{
      width: size, height: size, borderRadius: LT.r.md,
      background: s.bg, color: s.c, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Ico d={I[s.icon] || I.list} size={Math.round(size * 0.5)} stroke={s.c} />
    </div>
  );
};

// I limiti, come compaiono sulla card: una sola pillola azzurra.
// <span>, non <div>: compare anche dentro un paragrafo e un <div> lì dentro
// non è HTML valido.
const Limiti = ({ min, max, style }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 10,
    background: LT.bluSoft, color: LT.blu, padding: '4px 10px',
    borderRadius: LT.r.sm, fontSize: 12, fontWeight: 600,
    verticalAlign: 'middle', ...style,
  }}>
    <span>Min: <strong>{min}</strong></span>
    <span>Max: <strong>{max}</strong></span>
  </span>
);

// La card di un'attività, come compare in elenco.
const CardAttivita = ({ ritardo, frequenza, cat, tipo, oggetto, dove, soglie, ultima }) => (
  <div style={{
    background: LT.n0, border: `1px solid ${LT.n150}`,
    borderLeft: ritardo ? `4px solid ${LT.rosso}` : `1px solid ${LT.n150}`,
    borderRadius: LT.r.lg, padding: 14, marginBottom: 10,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
      <ChipFinto>Ogni {frequenza}gg</ChipFinto>
      {ritardo && <ChipFinto tono="rosso">In ritardo +{ritardo}gg</ChipFinto>}
    </div>
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <Tessera cat={cat} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14.5, fontWeight: 700, color: LT.n900, lineHeight: 1.3 }}>{oggetto}</div>
        <div style={{ fontSize: 12.5, color: LT.n600, marginTop: 3 }}>
          {dove} <span style={{ color: LT.n300 }}>·</span> {tipo}
        </div>
        {soglie && <Limiti min={soglie[0]} max={soglie[1]} style={{ marginTop: 8 }} />}
        <div style={{ fontSize: 12, color: LT.n500, marginTop: 6 }}>{ultima}</div>
      </div>
    </div>
    <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
      <span style={{ flex: 1 }}><BtnFinto tono="verde" pieno>Conforme</BtnFinto></span>
      <span style={{ flex: 1 }}><BtnFinto tono="rosso" pieno>Non conforme</BtnFinto></span>
    </div>
    <div style={{ marginTop: 6, textAlign: 'right', fontSize: 11, color: LT.n500, fontWeight: 600 }}>
      N.A. — non applicabile oggi
    </div>
  </div>
);

const CampoFinto = ({ etichetta, valore, larghezza }) => (
  <div style={{ width: larghezza || '100%' }}>
    <div style={{
      fontSize: 10.5, fontWeight: 700, color: LT.n600,
      textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 3,
    }}>{etichetta}</div>
    <div style={{
      border: `1px solid ${LT.n300}`, borderRadius: LT.r.md, padding: '7px 9px',
      background: LT.n0, fontSize: 13, color: valore ? LT.n900 : LT.n400,
    }}>{valore || '—'}</div>
  </div>
);

export default function Monitoraggi() {
  return (
    <GuidaLayout
      titolo="Registrare i monitoraggi"
      sottotitolo="La schermata che aprite ogni mattina: cosa va controllato oggi, come si registra in due tocchi, e cosa fare quando il valore non torna o l'attività non si può proprio fare."
      durata="~ 12 minuti"
      destinatario="Per operatori"
      sezioni={SEZIONI}
    >

      {/* m0 */}
      <H id="m0">La schermata di ogni giorno</H>

      <P>
        <Code>Attività da eseguire</Code> è l'elenco di quello che tocca a voi oggi:
        temperature da leggere, pulizie da fare, controlli da spuntare. Non lo compilate voi
        — nasce dallo <Strong>scadenziario</Strong>, che qualcuno ha impostato una volta e
        che da solo fa comparire le attività il giorno giusto.
      </P>
      <P>
        Il lavoro vostro è breve: guardare, fare, e dire com'è andata. In due tocchi.
      </P>

      <Callout kind="info" titolo="Se la lista è vuota, non è un errore">
        Vuol dire che per oggi non c'è niente in scadenza. Le attività compaiono quando
        arriva il loro turno: un controllo settimanale non si vede tutti i giorni. Con{' '}
        <Tasto>Mostra tutto</Tasto> si vedono anche quelle non ancora in scadenza.
      </Callout>

      {/* m1 */}
      <H id="m1" cat="temperature">1. Leggere la lista</H>

      <MockFrame
        url="app.labthree.it — Attività da eseguire"
        markers={[
          { n: 1, top: '14.6%', left: '28%', titolo: 'Classica o Settimanale',
            testo: 'Due modi di vedere le stesse cose. La scelta resta quella che avete fatto l’ultima volta.' },
          { n: 2, top: '27%', left: '34%', titolo: 'Filtro per ambito',
            testo: 'Compare solo se avete attività di ambiti diversi. Restringe la lista a Temperature, Pulizie e così via.' },
          { n: 3, top: '37%', left: '33%', titolo: 'In ritardo',
            testo: 'Bordo rosso e giorni di ritardo. Non sparisce e non si blocca: va fatta e registrata lo stesso.' },
          { n: 4, top: '48.6%', left: '34%', titolo: 'Min e Max',
            testo: 'Se ci sono, l’attività chiede un valore misurato. Sono i limiti oltre i quali scatta la non conformità.' },
        ]}
      >
        <Schermo>
          <div style={{
            fontSize: 10.5, color: LT.n500, fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '.08em',
          }}>Martedì 19 agosto</div>
          <div style={{ fontSize: 19, fontWeight: 700, color: LT.n900, marginTop: 3 }}>
            Attività da eseguire
          </div>
          <div style={{ fontSize: 12, color: LT.n500, marginBottom: 12 }}>Salumificio Demo S.r.l.</div>

          <div style={{ marginBottom: 12 }}>
            <Segmenti voci={['Classica', 'Settimanale']} attivo="Classica" />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
            <BtnFinto tono="blu">Avvia sessione</BtnFinto>
          </div>

          <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
            <ChipFinto tono="blu">Tutti</ChipFinto>
            <ChipFinto>Temperature</ChipFinto>
            <ChipFinto>Pulizie</ChipFinto>
          </div>

          <div style={{
            fontSize: 10.5, fontWeight: 700, color: LT.rosso,
            textTransform: 'uppercase', letterSpacing: '.08em', margin: '4px 2px 8px',
          }}>In ritardo</div>

          <CardAttivita
            ritardo={2}
            frequenza={1}
            cat="temperature"
            tipo="Controllo temperatura"
            oggetto="Cella 1 — sale"
            dove="Reparto salatura"
            soglie={['0 °C', '4 °C']}
            ultima="Ultimo: 17/08/2026"
          />
          <CardAttivita
            frequenza={7}
            cat="pulizie"
            tipo="Pulizia e sanificazione"
            oggetto="Piano di lavoro"
            dove="Laboratorio"
            ultima="Ultimo: 18/08/2026"
          />
        </Schermo>
      </MockFrame>

      <P>
        Le attività sono divise fra <Strong>in ritardo</Strong> — bordo rosso e il
        contrassegno con i giorni — e quelle in scadenza oggi, e dentro ciascun blocco sono
        raggruppate per ambito. La riga di pulsanti sotto il titolo le restringe a un ambito
        solo, e <Tasto>Mostra tutto</Tasto> aggiunge anche quelle che scadranno più avanti.
      </P>
      <P>
        Su ogni scheda il nome grande è <Strong>la cosa da controllare</Strong> — la cella, il
        piano di lavoro, il termometro — e la riga sotto dice dove si trova e che tipo di
        controllo è. Il contrassegno in alto (<Code>Ogni 7gg</Code>) ricorda ogni quanto
        torna.
      </P>

      <Callout kind="info" titolo="Le attività a «Controllo continuo»">
        Alcune voci non hanno una cadenza fissa e portano il contrassegno{' '}
        <Code>Controllo continuo</Code>: restano sempre in lista, perché non c'è un giorno in
        cui siano «fatte». Al posto della data dell'ultima volta mostrano da quante ore è
        stato registrato l'ultimo controllo — <em>Ultima esecuzione: 3,5h fa</em> — oppure{' '}
        <em>Nessuna esecuzione oggi</em>. Si registrano esattamente come le altre, anche più
        volte al giorno.
      </Callout>

      <Callout kind="warn" titolo="Il ritardo non si cancella saltandolo">
        Un'attività in ritardo resta lì finché non la registrate. È voluto: un registro con
        i buchi è peggio di un registro con una registrazione tardiva. Se l'avete fatta ieri
        e state registrando oggi, il capitolo 4 spiega come mettere data e ora giuste.
      </Callout>

      {/* m2 */}
      <H id="m2" cat="temperature">2. I tre pulsanti</H>

      <P>Ogni attività ha due pulsanti grandi e una scritta piccola. Sono tre risposte diverse.</P>

      <Passo n="1" titolo={<><Tasto>Conforme</Tasto> — è andato tutto bene</>}>
        <P style={{ margin: '4px 0' }}>
          Il controllo è stato fatto e il risultato è a posto. Se l'attività non prevede un
          valore, la registrazione è finita qui.
        </P>
      </Passo>

      <Passo n="2" titolo={<><Tasto>Non conforme</Tasto> — qualcosa non va</>}>
        <P style={{ margin: '4px 0' }}>
          Si apre subito la finestra della <Strong>non conformità</Strong>, già compilata con
          tipo di controllo, oggetto, luogo e — se c'è — il valore misurato con i limiti
          violati. Non dovete riscrivere niente: descrivete cosa avete fatto e chiudete.
        </P>
      </Passo>

      <Passo n="3" titolo={<><Tasto>N.A. — non applicabile oggi</Tasto> — non si poteva fare</>}>
        <P style={{ margin: '4px 0' }}>
          È la scritta piccola in basso a destra della scheda, e tenendoci sopra il mouse
          spiega da sé quando si usa: <em>es. macchina non in uso</em>. La macchina era
          spenta, la cella vuota, il reparto fermo. Registra un <Strong>N.A.</Strong>: non è
          né conforme né non conforme, ed è la risposta onesta quando il controllo non aveva
          senso.
        </P>
      </Passo>

      <Callout kind="tip" titolo="N.A. è meglio del silenzio">
        La tentazione è saltare l'attività e basta. Ma un buco nel registro, mesi dopo, non
        si distingue da una dimenticanza. Un N.A. dice <em>«quel giorno non si poteva
        fare»</em>, e resta scritto con la data e il vostro nome.
      </Callout>

      <P>
        Registrato l'esito, in basso compare per un attimo <Code>✓ Attività registrata!</Code>{' '}
        e l'attività sparisce dalla lista di oggi. Non c'è nient'altro da salvare.
      </P>

      <Callout kind="info" titolo="Alcune attività chiedono anche un documento">
        Tarature, manutenzioni, analisi e verifiche hanno quasi sempre una carta che le
        accompagna. Per queste, subito dopo la registrazione si apre da sola una finestra —{' '}
        <Code>Carica rapporto di taratura</Code>, <Code>Carica referto di analisi</Code> e
        così via — che vi propone di allegare il file (PDF o immagine, fino a 10 MB). È una
        proposta, non un obbligo: <Tasto>Salta — aggiungerò in seguito</Tasto> chiude tutto e
        il documento si può caricare più avanti dallo <Code>Storico</Code>. Ma se il rapporto
        ce l'avete lì, quello è il momento in cui costa meno.
      </Callout>

      {/* m3 */}
      <H id="m3" cat="temperature">3. Quando chiede un valore</H>

      <P>
        Se sulla scheda compare la pillola azzurra <Limiti min="0 °C" max="4 °C" />, premendo
        un pulsante si apre una finestra che chiede il <Strong>Valore misurato</Strong>. È il
        caso tipico delle temperature.
      </P>

      <MockFrame
        url="app.labthree.it — Valore misurato"
        didascalia="I limiti restano sotto gli occhi mentre scrivete, e il programma controlla che la risposta e il numero dicano la stessa cosa. In fondo, data e ora si possono correggere per questa sola registrazione."
      >
        <Schermo>
          <div style={{
            background: LT.n0, border: `1px solid ${LT.n150}`,
            borderRadius: LT.r.xl, padding: 20, maxWidth: 430, margin: '0 auto',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
              <div style={{
                width: 40, height: 40, borderRadius: LT.r.md,
                background: LT.verdeSoft, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Ico d={I.check} size={22} stroke={LT.verde} />
              </div>
              <div>
                <div style={{ fontSize: 15.5, fontWeight: 700, color: LT.n900 }}>Registra valore</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: LT.verde, marginTop: 2 }}>
                  Esito conforme
                </div>
              </div>
            </div>

            <div style={{
              background: LT.n100, border: `1px solid ${LT.n150}`,
              borderRadius: LT.r.md, padding: '10px 12px', marginBottom: 12,
            }}>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: LT.n900 }}>
                Cella 1 — sale — Reparto salatura
              </div>
              <div style={{ fontSize: 11.5, color: LT.n500, marginTop: 2 }}>
                Temperature · Controllo temperatura
              </div>
            </div>

            <div style={{ marginBottom: 10 }}>
              <Limiti min="0 °C" max="4 °C" />
            </div>

            <div style={{
              fontSize: 10.5, fontWeight: 700, color: LT.n500,
              textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 5,
            }}>Valore misurato</div>
            <div style={{
              border: `1px solid ${LT.n300}`, borderRadius: LT.r.md, padding: '13px 12px',
              background: LT.n0, textAlign: 'center', fontSize: 21, fontWeight: 700,
              fontFamily: LT.mono, color: LT.n900,
            }}>2,4</div>

            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <CampoFinto etichetta="Data (opzionale)" valore="19/08/2026" />
              <CampoFinto etichetta="Ora (opzionale)" valore="06:10" />
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <span style={{ flex: 1 }}><BtnFinto>Annulla</BtnFinto></span>
              <span style={{ flex: 1 }}><BtnFinto tono="verde" pieno>Conferma</BtnFinto></span>
            </div>
          </div>
        </Schermo>
      </MockFrame>

      <Callout kind="warn" titolo="Il programma non vi lascia dire due cose diverse">
        Se scrivete un valore fuori dai limiti e avete premuto <Tasto>Conforme</Tasto>, la
        registrazione si ferma: <em>«Valore fuori dai limiti (min: 0, max: 4) — non puoi
        registrare come CONFORME»</em>. E vale anche al contrario: un valore dentro i limiti
        non si può registrare come non conforme. Non è una scortesia — è il controllo che
        impedisce a un registro di contraddirsi da solo. La strada è tornare indietro e
        premere l'altro pulsante.
      </Callout>

      <P>
        Si può scrivere <Code>2,4</Code> o <Code>2.4</Code>, indifferentemente — e i valori
        sotto zero con il meno davanti, <Code>-18,5</Code>. Se quello che scrivete non è un
        numero il programma non lo registra e lo dice:{' '}
        <em>Inserisci un valore numerico valido</em>.
      </P>

      <Callout kind="tip" titolo="Data e ora, senza aprire una sessione">
        Sotto il valore ci sono due campi <Strong>Data (opzionale)</Strong> e{' '}
        <Strong>Ora (opzionale)</Strong>: se state registrando adesso un controllo fatto
        stamattina, basta scriverlo lì e vale solo per questa registrazione. La sessione del
        capitolo seguente serve quando ce n'è un giro intero da registrare, non per una
        singola. Se una sessione è già attiva quei due campi spariscono e al loro posto
        compare l'orario della sessione.
      </Callout>

      {/* m4 */}
      <H id="m4" cat="formazione">4. La sessione di registrazione</H>

      <P>
        Capita spesso: il giro dei controlli lo fate alle sei del mattino con le mani
        occupate, e le registrate al computer alle otto. Registrandole normalmente
        risulterebbero fatte alle otto, che non è vero.
      </P>
      <P>
        Il pulsante <Tasto>Avvia sessione</Tasto>, in alto a destra, risolve questo. Apre una
        finestrella — <Code>Avvia sessione di registrazione</Code> — con due campi,{' '}
        <Strong>Data</Strong> e <Strong>Ora</Strong>: ci mettete l'orario del giro vero, e da
        quel momento tutte le registrazioni che fate prendono quell'orario invece di adesso.
        Il pulsante lascia il posto a una striscia azzurra <Strong>Sessione attiva</Strong>
        con la data e l'ora scelte, e un pulsante <Tasto>Termina</Tasto> per chiuderla.
      </P>

      <MockFrame
        url="app.labthree.it — Sessione attiva"
        didascalia="Finché la striscia è lì, tutto quello che registrate prende quella data e quell'ora."
      >
        <Schermo>
          <div style={{
            background: LT.bluSoft, border: `1px solid ${LT.blu}22`, borderRadius: LT.r.md,
            padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <div style={{
              width: 32, height: 32, borderRadius: LT.r.md, background: LT.blu,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <Ico d={I.clock} size={18} stroke="#fff" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: LT.blu }}>Sessione attiva</div>
              <div style={{ fontSize: 12, color: LT.blu, marginTop: 1 }}>19/08/2026 alle 06:00</div>
            </div>
            <BtnFinto tono="rosso">Termina</BtnFinto>
          </div>
        </Schermo>
      </MockFrame>

      <Callout kind="warn" titolo="È solo vostra, e questo cambia il modo di usarla">
        La sessione vale <Strong>solo per voi e solo su questa scheda del browser</Strong>.
        Non la vedono i colleghi, e non tocca le loro registrazioni. Se ricaricate la pagina
        o chiudete la scheda, <Strong>si spegne da sola</Strong> — e le registrazioni
        successive tornano all'ora corrente senza avvisare.
      </Callout>

      <Callout kind="warn" titolo="Ricordarsi di chiuderla">
        Il rischio opposto è lasciarla accesa: finite di registrare il giro delle sei, poi
        nel pomeriggio registrate un altro controllo e finisce anche quello alle sei del
        mattino. Prima di registrare qualcosa fuori dal giro, guardate se in alto c'è ancora
        la striscia azzurra: in tal caso premete <Tasto>Termina</Tasto>.
      </Callout>

      {/* m5 */}
      <H id="m5" cat="temperature">5. La vista settimanale</H>

      <P>
        Il segmento <Tasto>Settimanale</Tasto> mostra le stesse attività disposte come una
        griglia: le righe sono le cose da controllare, le colonne i giorni. Serve a vedere{' '}
        <Strong>l'andamento</Strong> invece del singolo giorno — quali caselle sono piene e
        quali no.
      </P>
      <P>
        È utile a fine settimana, per accorgersi di un buco prima che diventi un buco nel
        registro. La scelta fra le due viste resta memorizzata: se preferite la settimanale,
        la ritrovate così anche domani.
      </P>

      {/* m6 */}
      <H id="m6" cat="pulizie">6. Correggere un errore</H>

      <P>
        Capita di premere il pulsante sbagliato o di digitare 12 invece di 1,2. La
        correzione <Strong>non si fa da questa schermata</Strong>: si fa da{' '}
        <Code>Storico monitoraggi</Code>, dove ogni registrazione già fatta ha accanto un
        pulsante <Tasto>Correggi</Tasto> (e, per le attività tecniche, un{' '}
        <Tasto>Allegati</Tasto> per aggiungere il documento in un secondo momento).
      </P>

      <div style={{
        border: `1px solid ${LT.n200}`, borderRadius: LT.r.md,
        padding: '14px 18px', margin: '18px 0', background: LT.n0,
      }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: LT.n500, letterSpacing: 0.5, marginBottom: 10 }}>
          COSA POTETE CORREGGERE DA SOLI
        </div>
        {[
          ['Solo le vostre', 'le registrazioni fatte da un collega le corregge lui, o un amministratore'],
          ['Entro sette giorni', 'passata quella finestra serve un amministratore'],
          ['Non ancora verificate', 'una volta che il responsabile l’ha verificata, la correzione è sua'],
        ].map(([t, d], i) => (
          <div key={i} style={{
            display: 'flex', gap: 12, padding: '8px 0', flexWrap: 'wrap',
            borderBottom: i < 2 ? `1px solid ${LT.n150}` : 'none',
          }}>
            <span style={{ flex: '0 0 170px', fontSize: 14, fontWeight: 700, color: LT.navy }}>{t}</span>
            <span style={{ flex: '1 1 240px', fontSize: 14, color: LT.n700, lineHeight: 1.5 }}>{d}</span>
          </div>
        ))}
      </div>

      <Callout kind="tip" titolo="Se il pulsante Correggi è spento, il programma dice perché">
        Non sparisce: resta lì disabilitato, e passandoci sopra col mouse compare il motivo —
        non è vostra, è già verificata, oppure sono passati più di sette giorni. In tutti e
        tre i casi la strada è la stessa: chiedere a un amministratore.
      </Callout>

      {/* m7 */}
      <H id="m7">Le trappole</H>

      <Callout kind="warn" titolo="La voce «Le mie attività» è grigia">
        Se dal menu la voce <Tasto>Le mie attività</Tasto> è spenta e non si preme, non è un
        guasto: vuol dire che <Strong>lo scadenziario non vi assegna ancora niente</Strong> —
        né come titolari né come supplenti. Non è un permesso da spuntare da qualche parte:
        la voce si accende da sola quando chi gestisce lo scadenziario vi mette in carico
        almeno un'attività. Il controllo avviene all'ingresso: se l'assegnazione arriva
        mentre siete già dentro, uscite e rientrate col PIN.
      </Callout>

      <Callout kind="warn" titolo="Registrare a fine giornata tutto insieme">
        Funziona, ma è il modo migliore per dimenticare un valore o sbagliarne uno. Se
        proprio si fa, si usa la <Strong>sessione</Strong> con l'ora vera del giro, non
        l'ora in cui si sta scrivendo.
      </Callout>

      <Callout kind="warn" titolo="Non conforme non è una colpa">
        La tentazione di registrare conforme «tanto poi sistemo» è forte, ed è l'errore più
        grave che si possa fare qui. Un valore fuori soglia registrato e gestito è un
        sistema che funziona; lo stesso valore registrato come conforme è un registro falso,
        e in un controllo pesa molto di più del problema che nascondeva.
      </Callout>

      {/* m8 */}
      <H id="m8">Domande frequenti</H>

      <div style={{ marginTop: 8 }}>
        <FAQ q="Ho premuto Conforme per sbaglio. Posso rimediare?">
          Sì, dallo <Code>Storico</Code>, entro sette giorni e finché la registrazione non è
          stata verificata. Se una di queste due condizioni è saltata, serve un
          amministratore — ma la correzione resta possibile, non si perde niente.
        </FAQ>

        <FAQ q="Un'attività è in ritardo di dieci giorni: la registro lo stesso?">
          Sì. Un buco nel registro è peggio di una registrazione tardiva, e la data reale
          resta scritta. Se il ritardo si ripete sempre sulla stessa attività, il problema
          non è vostro: è la frequenza impostata nello scadenziario, che va rivista.
        </FAQ>

        <FAQ q="Che differenza c'è fra Non applicabile e non registrare niente?">
          Enorme. <Strong>Non applicabile</Strong> è una risposta: resta scritta con data,
          ora e il vostro nome, e dice che quel giorno il controllo non aveva senso. Non
          registrare niente lascia una casella vuota che, a distanza di mesi, nessuno sa più
          distinguere da una dimenticanza.
        </FAQ>

        <FAQ q="Devo registrare anche se il valore è perfetto?">
          Sì, ed è il punto di tutto: il registro serve a dimostrare che il controllo è
          stato fatto, non solo che qualcosa è andato storto. Le registrazioni conformi sono
          la maggior parte, e sono quelle che dimostrano che il sistema è vivo.
        </FAQ>

        <FAQ q="Posso registrare dal telefono?">
          Sì, la schermata è pensata anche per lo schermo piccolo, ed è il modo più comodo:
          si registra davanti alla cella invece di ricordarsi il numero fino all'ufficio.
          Si può anche aggiungere LabThree alla schermata Home del telefono, così si apre
          come un'applicazione.
        </FAQ>

        <FAQ q="Perché vedo un'attività che non è mia?">
          Perché nello scadenziario risultate <Strong>supplenti</Strong> su quella voce: la
          lista mostra le attività di cui siete il responsabile del monitoraggio più quelle
          in cui siete indicati come sostituto. È fatto apposta, così quando il titolare è in
          ferie o malattia il controllo non resta scoperto. Se l'assegnazione non vi
          convince, parlatene con chi ha impostato lo scadenziario: si cambia in un attimo.
        </FAQ>
      </div>

      <div style={{
        marginTop: 40, padding: '18px 20px', background: LT.bluSoft,
        borderRadius: LT.r.md, display: 'flex', gap: 14, alignItems: 'flex-start',
      }}>
        <Ico d={I.arrowRight} size={20} stroke={LT.blu} style={{ marginTop: 2 }} />
        <div>
          <div style={{ fontWeight: 700, color: LT.blu, fontSize: 14, marginBottom: 4 }}>
            La guida dopo questa
          </div>
          <div style={{ fontSize: 14, color: LT.n700, lineHeight: 1.6 }}>
            <Strong>Gestire una non conformità</Strong>: cosa succede dopo aver premuto{' '}
            <Tasto>Non conforme</Tasto>, e come si chiude bene una pratica.
          </div>
        </div>
      </div>

    </GuidaLayout>
  );
}
